import classNames from 'classnames';
import { ComponentProps, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import useGroupFocus from '@alltrails/core/hooks/useGroupFocus';
import Clear from '../../icons/Clear';
import Search from '../../icons/Search';
import { Size } from '../../types';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import IconButton from '../IconButton';
import LoadingSpinner from '../LoadingSpinner';
import styles from './styles/styles.module.scss';

export type SearchInputProps = {
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
  /**
   * An override for default "clear" functionality of the icon button in the input.
   */
  iconButtonProps?: Pick<ComponentProps<typeof IconButton>, 'icon' | 'onClick' | 'title'>;
  onBlur?: () => void;
  onChange: (value: string) => void;
  onClick?: () => void;
  /** Called after the default "clear" of the icon button, so a parent can drop what the value came from */
  onClear?: () => void;
  onFocus?: () => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  placeholder?: string;
  value?: string;
  variant?: 'default' | 'subtle';
  size?: Size<'sm' | 'md'>;
  searchRequestLoading?: boolean;
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'onBlur' | 'onFocus' | 'onKeyDown' | 'onClick' | 'value' | 'disabled' | 'placeholder' | 'autoFocus' | 'className' | 'size'
>;

const SearchInput = ({
  autoFocus,
  className,
  disabled,
  iconButtonProps,
  onBlur,
  onChange,
  onClick,
  onClear,
  onFocus,
  onKeyDown,
  placeholder,
  value,
  variant = 'default',
  size = 'md',
  searchRequestLoading,
  ...restProps
}: SearchInputProps) => {
  const intl = useIntl();
  const inputElement = useRef<HTMLInputElement>(null);
  const { blur, focus } = useGroupFocus(onFocus, onBlur);

  useEffect(() => {
    if (autoFocus) {
      inputElement.current?.focus();
    }
  }, [autoFocus]);

  const iconButton = useMemo(() => {
    if (searchRequestLoading) {
      return <LoadingSpinner className={styles.loadingSpinner} size="md" />;
    }

    if (value) {
      return (
        <IconButton
          className={styles.inputIconButton}
          icon={iconButtonProps?.icon ?? { Component: Clear }}
          onClick={e => {
            if (iconButtonProps?.onClick) {
              iconButtonProps.onClick(e);
            } else {
              onChange('');
              onClear?.();
              inputElement.current?.focus();
            }
          }}
          onBlur={blur}
          onFocus={focus}
          variant="ghost"
          size={size}
          stopPropagation
          title={iconButtonProps?.title ?? intl.formatMessage({ defaultMessage: 'Clear' })}
        />
      );
    }

    return null;
  }, [blur, focus, iconButtonProps, intl, onChange, onClear, size, value, searchRequestLoading]);

  return (
    <div
      {...setDenaliAttribute('SearchInput')}
      className={classNames(styles.container, disabled && styles.disabled, styles[variant], styles[size], className)}
    >
      <Search color={disabled ? '--color-text-disabled' : '--color-text-primary'} size="sm" />
      <input
        ref={inputElement}
        className={classNames(styles.input, styles[size])}
        disabled={disabled}
        onChange={e => {
          onChange(e.target.value);
        }}
        onBlur={blur}
        onFocus={focus}
        onKeyDown={onKeyDown}
        onClick={onClick}
        placeholder={placeholder}
        value={value}
        {...(!restProps['aria-labelledby'] && {
          'aria-label': placeholder || intl.formatMessage({ defaultMessage: 'Search input' })
        })}
        {...restProps}
      />
      {iconButton}
    </div>
  );
};

export default SearchInput;
